/**
 * FormDocumentEditor — Google-Docs-style editor for clinic forms.
 *
 * Layout: sticky formatting toolbar, A4 "paper" canvas, and an insert-blocks
 * sidebar (client signature slots, therapist signature, signature date,
 * free-text field, patient fields, section, page break, phonetic symbols).
 *
 * Output: HTML string (via onChange) that follows the legacy form contract —
 * see ../formPlaceholders.js. Legacy content is normalized on load.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import {
  ArrowUUpLeft,
  ArrowUUpRight,
  TextB,
  TextItalic,
  TextUnderline,
  TextStrikethrough,
  Palette,
  TextAlignRight,
  TextAlignCenter,
  TextAlignLeft,
  TextAlignJustify,
  ListBullets,
  ListNumbers,
  Quotes,
  Minus,
  Table as TableIcon,
  Eraser,
  PencilLine,
  CalendarBlank,
  TextT,
  User,
  Microphone,
  ArrowsOutLineVertical,
  TextHOne,
  Columns as ColumnsIcon,
  SquareSplitHorizontal,
  Plus,
  X,
  MagnifyingGlass,
  CaretDown,
  Rows,
  Columns,
  TrashSimple,
} from "@phosphor-icons/react";
import {
  SignatureSlot,
  TherapistSignature,
  PageBreak,
  PatientField,
  FreeTextField,
  SignatureDate,
  LegacyTextSize,
  FormTable,
  buildLayoutTable,
} from "./formExtensions";
import {
  PATIENT_FIELD_PLACEHOLDERS,
  TEXT_COLORS,
  PHONETIC_SYMBOLS,
  PHONETIC_TABS,
  MAX_SIGNATURE_SLOTS,
  normalizeLegacyFormHtml,
} from "../formPlaceholders";
import SignaturePadModal from "../../ui/SignaturePadModal";
import "../../../styles/formDocument.css";
import "./FormDocumentEditor.css";

const HEADING_OPTIONS = [
  { value: "paragraph", label: "טקסט רגיל" },
  { value: "h1", label: "כותרת ראשית" },
  { value: "h2", label: "כותרת" },
  { value: "h3", label: "כותרת משנה" },
];

const ALIGN_OPTIONS = [
  { value: "right", icon: TextAlignRight, title: "יישור לימין" },
  { value: "center", icon: TextAlignCenter, title: "מרכוז" },
  { value: "left", icon: TextAlignLeft, title: "יישור לשמאל" },
  { value: "justify", icon: TextAlignJustify, title: "יישור לשני הצדדים" },
];

/* ---------------- Small UI helpers ---------------- */

function ToolbarButton({ icon: Icon, title, active, disabled, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      className={`fde-tb-btn${active ? " is-active" : ""} ${className}`}
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {Icon && <Icon size={18} weight={active ? "bold" : "regular"} />}
      {children}
    </button>
  );
}

function useClickOutside(ref, onClose, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onClose, enabled]);
}

/* ---------------- Toolbar ---------------- */

function Toolbar({ editor }) {
  const [colorOpen, setColorOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const colorRef = useRef(null);
  const tableRef = useRef(null);
  useClickOutside(colorRef, () => setColorOpen(false), colorOpen);
  useClickOutside(tableRef, () => setTableOpen(false), tableOpen);

  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) return null;
      return {
        canUndo: ed.can().undo(),
        canRedo: ed.can().redo(),
        bold: ed.isActive("bold"),
        italic: ed.isActive("italic"),
        underline: ed.isActive("underline"),
        strike: ed.isActive("strike"),
        bulletList: ed.isActive("bulletList"),
        orderedList: ed.isActive("orderedList"),
        blockquote: ed.isActive("blockquote"),
        inTable: ed.isActive("table"),
        heading: ed.isActive("heading", { level: 1 })
          ? "h1"
          : ed.isActive("heading", { level: 2 })
          ? "h2"
          : ed.isActive("heading", { level: 3 })
          ? "h3"
          : "paragraph",
        align: ed.isActive({ textAlign: "center" })
          ? "center"
          : ed.isActive({ textAlign: "left" })
          ? "left"
          : ed.isActive({ textAlign: "justify" })
          ? "justify"
          : "right",
        color: ed.getAttributes("textStyle").color || "",
      };
    },
  });

  if (!editor || !state) return null;
  const chain = () => editor.chain().focus();

  const setHeading = (value) => {
    if (value === "paragraph") chain().setParagraph().run();
    else chain().toggleHeading({ level: Number(value.slice(1)) }).run();
  };

  return (
    <div className="fde-toolbar" role="toolbar" aria-label="עיצוב טקסט">
      <div className="fde-tb-group">
        <ToolbarButton icon={ArrowUUpRight} title="בטל" disabled={!state.canUndo} onClick={() => chain().undo().run()} />
        <ToolbarButton icon={ArrowUUpLeft} title="בצע שוב" disabled={!state.canRedo} onClick={() => chain().redo().run()} />
      </div>

      <div className="fde-tb-group">
        <div className="fde-select-wrap">
          <select
            className="fde-select"
            value={state.heading}
            onChange={(e) => setHeading(e.target.value)}
            aria-label="סגנון פסקה"
          >
            {HEADING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <CaretDown size={12} weight="bold" className="fde-select-caret" />
        </div>
      </div>

      <div className="fde-tb-group">
        <ToolbarButton icon={TextB} title="מודגש (Ctrl+B)" active={state.bold} onClick={() => chain().toggleBold().run()} />
        <ToolbarButton icon={TextItalic} title="נטוי (Ctrl+I)" active={state.italic} onClick={() => chain().toggleItalic().run()} />
        <ToolbarButton icon={TextUnderline} title="קו תחתון (Ctrl+U)" active={state.underline} onClick={() => chain().toggleUnderline().run()} />
        <ToolbarButton icon={TextStrikethrough} title="קו חוצה" active={state.strike} onClick={() => chain().toggleStrike().run()} />
        <div className="fde-dropdown" ref={colorRef}>
          <ToolbarButton
            icon={Palette}
            title="צבע טקסט"
            active={!!state.color}
            onClick={() => setColorOpen((v) => !v)}
          >
            <span className="fde-color-dot" style={{ background: state.color || "currentColor" }} />
          </ToolbarButton>
          {colorOpen && (
            <div className="fde-menu fde-color-menu">
              <div className="fde-color-grid">
                {TEXT_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={`fde-color-swatch${state.color === c.value ? " is-active" : ""}`}
                    style={{ background: c.value }}
                    title={c.name}
                    aria-label={c.name}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      chain().setColor(c.value).run();
                      setColorOpen(false);
                    }}
                  />
                ))}
              </div>
              <button
                type="button"
                className="fde-menu-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  chain().unsetColor().run();
                  setColorOpen(false);
                }}
              >
                <Eraser size={14} />
                <span>ללא צבע</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="fde-tb-group">
        {ALIGN_OPTIONS.map((a) => (
          <ToolbarButton
            key={a.value}
            icon={a.icon}
            title={a.title}
            active={state.align === a.value}
            onClick={() => chain().setTextAlign(a.value).run()}
          />
        ))}
      </div>

      <div className="fde-tb-group">
        <ToolbarButton icon={ListBullets} title="רשימת תבליטים" active={state.bulletList} onClick={() => chain().toggleBulletList().run()} />
        <ToolbarButton icon={ListNumbers} title="רשימה ממוספרת" active={state.orderedList} onClick={() => chain().toggleOrderedList().run()} />
        <ToolbarButton icon={Quotes} title="ציטוט" active={state.blockquote} onClick={() => chain().toggleBlockquote().run()} />
        <ToolbarButton icon={Minus} title="קו מפריד" onClick={() => chain().setHorizontalRule().run()} />
        <div className="fde-dropdown" ref={tableRef}>
          <ToolbarButton icon={TableIcon} title="טבלה" active={state.inTable} onClick={() => setTableOpen((v) => !v)} />
          {tableOpen && (
            <div className="fde-menu">
              {!state.inTable ? (
                <button
                  type="button"
                  className="fde-menu-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    chain().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run();
                    setTableOpen(false);
                  }}
                >
                  <TableIcon size={14} />
                  <span>הוסף טבלה (3×2)</span>
                </button>
              ) : (
                <>
                  <button type="button" className="fde-menu-item" onMouseDown={(e) => e.preventDefault()} onClick={() => chain().addRowAfter().run()}>
                    <Rows size={14} />
                    <span>הוסף שורה</span>
                  </button>
                  <button type="button" className="fde-menu-item" onMouseDown={(e) => e.preventDefault()} onClick={() => chain().addColumnAfter().run()}>
                    <Columns size={14} />
                    <span>הוסף עמודה</span>
                  </button>
                  <button type="button" className="fde-menu-item" onMouseDown={(e) => e.preventDefault()} onClick={() => chain().deleteRow().run()}>
                    <Rows size={14} />
                    <span>מחק שורה</span>
                  </button>
                  <button type="button" className="fde-menu-item" onMouseDown={(e) => e.preventDefault()} onClick={() => chain().deleteColumn().run()}>
                    <Columns size={14} />
                    <span>מחק עמודה</span>
                  </button>
                  <button
                    type="button"
                    className="fde-menu-item is-danger"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      chain().deleteTable().run();
                      setTableOpen(false);
                    }}
                  >
                    <TrashSimple size={14} />
                    <span>מחק טבלה</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="fde-tb-group">
        <ToolbarButton
          icon={Eraser}
          title="נקה עיצוב"
          onClick={() => chain().unsetAllMarks().clearNodes().run()}
        />
      </div>
    </div>
  );
}

/* ---------------- Insert-blocks sidebar ---------------- */

function BlocksPanel({ editor, signatureCount, onOpenTherapistSignature, onClose }) {
  const [fieldSearch, setFieldSearch] = useState("");
  const [freeTextLabel, setFreeTextLabel] = useState("");
  const [freeTextOpen, setFreeTextOpen] = useState(false);
  const [phoneticOpen, setPhoneticOpen] = useState(false);
  const [phoneticTab, setPhoneticTab] = useState("consonants");
  const [fieldsOpen, setFieldsOpen] = useState(true);

  const nextSlot = signatureCount + 1;
  const canAddSignature = nextSlot <= MAX_SIGNATURE_SLOTS;

  const filteredFields = useMemo(() => {
    const q = fieldSearch.trim();
    if (!q) return PATIENT_FIELD_PLACEHOLDERS;
    return PATIENT_FIELD_PLACEHOLDERS.filter(
      (f) => f.label.includes(q) || f.group.includes(q)
    );
  }, [fieldSearch]);

  if (!editor) return null;
  const chain = () => editor.chain().focus();
  const afterInsert = () => {
    if (onClose) onClose();
  };

  const insertBlock = (type, attrs = {}) => {
    chain().insertContent([{ type, attrs }, { type: "paragraph" }]).run();
    afterInsert();
  };
  const insertInline = (type, attrs = {}) => {
    chain().insertContent([{ type, attrs }, { type: "text", text: " " }]).run();
    afterInsert();
  };
  const insertSection = () => {
    chain()
      .insertContent([
        { type: "heading", attrs: { level: 2 }, content: [{ type: "text", text: "כותרת סעיף" }] },
        { type: "paragraph" },
      ])
      .run();
    afterInsert();
  };
  const submitFreeText = () => {
    const label = freeTextLabel.trim();
    if (!label) return;
    insertInline("freeTextField", { label });
    setFreeTextLabel("");
    setFreeTextOpen(false);
  };

  return (
    <aside className="fde-blocks" aria-label="הוספת רכיבים לטופס">
      <div className="fde-blocks-header">
        <h3>הוספה לטופס</h3>
        <p>הרכיבים נוספים במקום הסמן — אפשר לגרור אותם למיקום אחר</p>
        {onClose && (
          <button type="button" className="fde-blocks-close" onClick={onClose} aria-label="סגור">
            <X size={18} weight="bold" />
          </button>
        )}
      </div>

      <div className="fde-blocks-section">
        <span className="fde-blocks-label">חתימות</span>
        <button
          type="button"
          className="fde-block-btn fde-block-btn--signature"
          onClick={() => insertBlock("signatureSlot", { slotId: String(nextSlot) })}
          disabled={!canAddSignature}
          title={canAddSignature ? `הוסף מקום לחתימת לקוח (${nextSlot}/${MAX_SIGNATURE_SLOTS})` : `ניתן להוסיף עד ${MAX_SIGNATURE_SLOTS} חתימות לקוח`}
        >
          <PencilLine size={18} weight="duotone" />
          <span className="fde-block-text">
            <span>{signatureCount === 0 ? "חתימת לקוח" : `חתימת לקוח ${nextSlot}`}</span>
            <small>{canAddSignature ? "הלקוח יחתום במקום הזה" : `מקסימום ${MAX_SIGNATURE_SLOTS} חתימות`}</small>
          </span>
        </button>
        <button type="button" className="fde-block-btn" onClick={onOpenTherapistSignature}>
          <PencilLine size={18} weight="regular" />
          <span className="fde-block-text">
            <span>חתימת מטפל</span>
            <small>ציור חתימה שלך שמוטמעת בטופס</small>
          </span>
        </button>
        <button type="button" className="fde-block-btn" onClick={() => insertInline("signatureDate")}>
          <CalendarBlank size={18} weight="regular" />
          <span className="fde-block-text">
            <span>תאריך חתימה</span>
            <small>מוחלף אוטומטית בתאריך החתימה</small>
          </span>
        </button>
      </div>

      <div className="fde-blocks-section">
        <span className="fde-blocks-label">מילוי על ידי הלקוח</span>
        {!freeTextOpen ? (
          <button type="button" className="fde-block-btn" onClick={() => setFreeTextOpen(true)}>
            <TextT size={18} weight="regular" />
            <span className="fde-block-text">
              <span>שדה טקסט חופשי</span>
              <small>הלקוח ימלא לפני החתימה</small>
            </span>
          </button>
        ) : (
          <div className="fde-inline-form">
            <label htmlFor="fde-free-text-label">שם השדה</label>
            <input
              id="fde-free-text-label"
              type="text"
              value={freeTextLabel}
              onChange={(e) => setFreeTextLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitFreeText();
                if (e.key === "Escape") setFreeTextOpen(false);
              }}
              placeholder="לדוגמא: סיבת הפנייה"
              autoFocus
            />
            <div className="fde-inline-form-actions">
              <button type="button" className="btn-secondary" onClick={() => setFreeTextOpen(false)}>
                ביטול
              </button>
              <button type="button" className="btn-primary" onClick={submitFreeText} disabled={!freeTextLabel.trim()}>
                <Plus size={14} weight="bold" />
                <span>הוסף</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="fde-blocks-section">
        <button
          type="button"
          className="fde-blocks-toggle"
          onClick={() => setFieldsOpen((v) => !v)}
          aria-expanded={fieldsOpen}
        >
          <User size={16} weight="regular" />
          <span>שדות מטופל</span>
          <CaretDown size={14} weight="bold" className={`fde-caret${fieldsOpen ? " is-open" : ""}`} />
        </button>
        {fieldsOpen && (
          <>
            <p className="fde-blocks-hint">מוחלפים בערכים מתיק המטופל בעת השליחה</p>
            <div className="fde-search">
              <MagnifyingGlass size={14} />
              <input
                type="text"
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                placeholder="חיפוש שדה..."
                aria-label="חיפוש שדה מטופל"
              />
            </div>
            <div className="fde-field-list">
              {filteredFields.map((item, idx) => {
                const prevGroup = idx > 0 ? filteredFields[idx - 1].group : null;
                return (
                  <div key={item.field}>
                    {item.group !== prevGroup && <div className="fde-field-group">{item.group}</div>}
                    <button
                      type="button"
                      className="fde-field-item"
                      onClick={() => insertInline("patientField", { field: item.field, label: item.placeholder })}
                    >
                      <span>{item.label}</span>
                      <code>{item.placeholder}</code>
                    </button>
                  </div>
                );
              })}
              {filteredFields.length === 0 && <p className="fde-blocks-hint">לא נמצאו שדות</p>}
            </div>
          </>
        )}
      </div>

      <div className="fde-blocks-section">
        <span className="fde-blocks-label">מבנה</span>
        <button type="button" className="fde-block-btn" onClick={insertSection}>
          <TextHOne size={18} weight="regular" />
          <span className="fde-block-text">
            <span>סעיף חדש</span>
            <small>כותרת + פסקה</small>
          </span>
        </button>
        <button type="button" className="fde-block-btn" onClick={() => { chain().insertContent([buildLayoutTable(2), { type: "paragraph" }]).run(); afterInsert(); }}>
          <SquareSplitHorizontal size={18} weight="regular" />
          <span className="fde-block-text">
            <span>חלוקה ל-2 עמודות</span>
            <small>בלוק שקוף עם שני תאים זה לצד זה</small>
          </span>
        </button>
        <button type="button" className="fde-block-btn" onClick={() => { chain().insertContent([buildLayoutTable(3), { type: "paragraph" }]).run(); afterInsert(); }}>
          <ColumnsIcon size={18} weight="regular" />
          <span className="fde-block-text">
            <span>חלוקה ל-3 עמודות</span>
            <small>בלוק שקוף עם שלושה תאים</small>
          </span>
        </button>
        <button type="button" className="fde-block-btn" onClick={() => insertBlock("pageBreak")}>
          <ArrowsOutLineVertical size={18} weight="regular" />
          <span className="fde-block-text">
            <span>מעבר עמוד</span>
            <small>מתחיל עמוד חדש ב-PDF</small>
          </span>
        </button>
      </div>

      <div className="fde-blocks-section">
        <button
          type="button"
          className="fde-blocks-toggle"
          onClick={() => setPhoneticOpen((v) => !v)}
          aria-expanded={phoneticOpen}
        >
          <Microphone size={16} weight="regular" />
          <span>כתב פונטי (IPA)</span>
          <CaretDown size={14} weight="bold" className={`fde-caret${phoneticOpen ? " is-open" : ""}`} />
        </button>
        {phoneticOpen && (
          <>
            <div className="fde-phonetic-tabs">
              {PHONETIC_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`fde-phonetic-tab${phoneticTab === tab.id ? " is-active" : ""}`}
                  onClick={() => setPhoneticTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="fde-phonetic-grid">
              {PHONETIC_SYMBOLS[phoneticTab].map((item, i) => (
                <button
                  key={`${item.symbol}-${i}`}
                  type="button"
                  className="fde-phonetic-btn"
                  title={item.name}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chain().insertContent(item.symbol).run()}
                >
                  <span className="fde-phonetic-symbol">{item.symbol}</span>
                  <span className="fde-phonetic-name">{item.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

/* ---------------- Main editor ---------------- */

const extensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    code: false,
    codeBlock: false,
    link: { openOnClick: false, autolink: true },
    dropcursor: { width: 2 },
  }),
  Placeholder.configure({
    placeholder: "התחילו לכתוב את תוכן הטופס כאן…",
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"],
    alignments: ["right", "center", "left", "justify"],
    // null → no inline style for the natural (RTL start) alignment
    defaultAlignment: null,
  }),
  TextStyle,
  Color,
  FormTable.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  SignatureSlot,
  TherapistSignature,
  PageBreak,
  PatientField,
  FreeTextField,
  SignatureDate,
  LegacyTextSize,
];

/**
 * @param {string} value - Initial HTML (only read on mount / when `resetKey` changes)
 * @param {(html: string) => void} onChange
 * @param {boolean} readOnly
 * @param {string|number} resetKey - change to reload `value` into the editor
 */
export default function FormDocumentEditor({ value = "", onChange, readOnly = false, resetKey }) {
  const [showTherapistModal, setShowTherapistModal] = useState(false);
  const [mobileBlocksOpen, setMobileBlocksOpen] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const initialContent = useMemo(() => normalizeLegacyFormHtml(value || ""), [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const editor = useEditor(
    {
      extensions,
      content: initialContent,
      editable: !readOnly,
      editorProps: {
        attributes: {
          class: "ck-form-doc fde-paper-content",
          dir: "rtl",
          lang: "he",
          spellcheck: "true",
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (onChangeRef.current) onChangeRef.current(ed.getHTML());
      },
    },
    [resetKey]
  );

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  const signatureCount = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) return 0;
      let count = 0;
      ed.state.doc.descendants((node) => {
        if (node.type.name === "signatureSlot") count += 1;
      });
      return count;
    },
  });

  const handleTherapistSignature = useCallback(
    (dataUrl) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent([{ type: "therapistSignature", attrs: { src: dataUrl } }, { type: "paragraph" }])
        .run();
      setShowTherapistModal(false);
      setMobileBlocksOpen(false);
    },
    [editor]
  );

  return (
    <div className={`fde${readOnly ? " is-readonly" : ""}`}>
      {!readOnly && <Toolbar editor={editor} />}

      <div className="fde-body">
        <div className="fde-canvas">
          <div className="fde-paper">
            <EditorContent editor={editor} />
          </div>
        </div>

        {!readOnly && (
          <div className={`fde-side${mobileBlocksOpen ? " is-open" : ""}`}>
            <BlocksPanel
              editor={editor}
              signatureCount={signatureCount || 0}
              onOpenTherapistSignature={() => setShowTherapistModal(true)}
              onClose={mobileBlocksOpen ? () => setMobileBlocksOpen(false) : null}
            />
          </div>
        )}
      </div>

      {!readOnly && (
        <>
          <button
            type="button"
            className="fde-fab"
            onClick={() => setMobileBlocksOpen(true)}
            aria-label="הוספה לטופס"
          >
            <Plus size={22} weight="bold" />
          </button>
          {mobileBlocksOpen && <div className="fde-side-backdrop" onClick={() => setMobileBlocksOpen(false)} />}
        </>
      )}

      {showTherapistModal && (
        <SignaturePadModal
          title="חתימת מטפל"
          hint="חתמו באמצעות העכבר או מסך מגע. החתימה תוטמע בטופס במקום הסמן."
          confirmLabel="הוסף לטופס"
          onConfirm={handleTherapistSignature}
          onClose={() => setShowTherapistModal(false)}
        />
      )}
    </div>
  );
}
